import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Alert from '@common/components/alert';
import { normaliseDomain } from './domain';
import type { ResearchErrorKind } from '../../hooks/use-trial-research';

type Props = {
  initialValue: string;
  error: ResearchErrorKind | null;
  isSubmitting: boolean;
  onSubmit: (domain: string) => void;
};

const ERROR_KEYS: Record<ResearchErrorKind, string> = {
  not_allowed: 'authentication:trial_domain_not_allowed',
  rate_limited: 'authentication:trial_domain_rate_limited',
  invalid: 'authentication:trial_domain_invalid',
  failed: 'authentication:trial_research_failed',
};

export const DomainStep = ({
  initialValue, error, isSubmitting, onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(initialValue);
  const domain = normaliseDomain(value);
  const ready = !!domain && !isSubmitting;

  useEffect(() => {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    document.getElementById('trial-domain')?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (domain) onSubmit(domain);
  };

  return (
    <div
      className={
        'FreeTrial__Signup max-md:!tw-items-stretch max-md:!tw-px-5 max-md:!tw-pb-9 '
        + 'max-md:!tw-pt-[60px]'
      }
    >
      <div
        className={
          'FreeTrial__Signup__Inner max-md:!tw-w-full max-md:!tw-flex-col '
          + 'max-md:!tw-items-stretch max-md:!tw-gap-[30px]'
        }
      >
        <div className="FreeTrial__Signup__Copy max-md:!tw-flex-none max-md:!tw-gap-[30px]">
          <span className="FreeTrial__Badge max-md:!tw-px-3 max-md:!tw-py-[7px] max-md:!tw-text-[13px]">
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="#7ea3ff">
              <path d="M12 2l2.2 6.8L21 11l-6.8 2.2L12 20l-2.2-6.8L3 11l6.8-2.2z" />
            </svg>
            {t('authentication:trial_signup_badge')}
          </span>
          <h1 className="max-md:!tw-text-[50px]">
            {t('authentication:trial_signup_title')}
            {' '}
            <span>{t('authentication:trial_signup_title_highlight')}</span>
          </h1>
          <form className="max-md:tw-flex max-md:tw-flex-col max-md:tw-gap-3" onSubmit={handleSubmit}>
            {error && (
            <Alert type="warning">
              {error === 'failed' ? (
                <Trans
                  i18nKey="authentication:trial_research_failed"
                  components={[
                    <a
                      key="helpdesk"
                      onClick={() => window.Intercom && window.Intercom('show')}
                      role="link"
                    />,
                  ]}
                />
              ) : (
                t(ERROR_KEYS[error])
              )}
            </Alert>
          )}
            <label className="max-md:!tw-mb-0 max-md:!tw-text-[12px]" htmlFor="trial-domain">
              {t('authentication:trial_signup_domain_label')}
            </label>
            <div
              className={
                'FreeTrial__DomainField max-md:!tw-h-auto max-md:!tw-flex-col '
                + 'max-md:!tw-items-stretch max-md:!tw-gap-3 max-md:!tw-border-0 '
                + 'max-md:!tw-bg-transparent max-md:!tw-p-0 max-md:!tw-shadow-none'
              }
            >
              <div
                className={
                  'FreeTrial__DomainPill max-md:!tw-h-[58px] max-md:!tw-min-h-[58px] '
                  + 'max-md:!tw-flex-none max-md:!tw-gap-2.5 max-md:!tw-rounded-2xl '
                  + 'max-md:!tw-border-[1.5px] max-md:!tw-border-[rgba(199,210,254,.3)] '
                  + 'max-md:!tw-bg-[rgba(250,250,250,.06)] max-md:!tw-px-[18px]'
                }
              >
                <svg
                  aria-hidden="true"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b96c9"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
                </svg>
                <input
                  id="trial-domain"
                  name="company-website"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="yourcompany.com"
                  type="text"
                  autoComplete="off"
                  inputMode="url"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
              <button className="max-md:!tw-h-14 max-md:!tw-w-full max-md:!tw-text-[17px]" type="submit" disabled={!ready}>
                {t('authentication:trial_signup_start')}
                <svg
                  aria-hidden="true"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </form>
        </div>
        <section
          className={
            'FreeTrial__How max-md:!tw-w-full max-md:!tw-flex-none max-md:!tw-border-0 '
            + 'max-md:!tw-bg-transparent max-md:!tw-p-0'
          }
          aria-labelledby="trial-how-title"
        >
          <h2 className="max-md:tw-hidden" id="trial-how-title">{t('authentication:trial_how_title')}</h2>
          <ol className="max-md:!tw-gap-3.5">
            {[1, 2, 3].map((n) => (
              <li className="max-md:!tw-items-start max-md:!tw-gap-3" key={n}>
                <span>{n}</span>
                <div>
                  <strong className="max-md:!tw-pt-0 max-md:!tw-text-[15px]">{t(`authentication:trial_how_${n}_title`)}</strong>
                  <p className="max-md:!tw-text-[13px]">{t(`authentication:trial_how_${n}_body`)}</p>
                </div>
              </li>
          ))}
          </ol>
        </section>
      </div>
    </div>
  );
};
